import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
    getStripeCardsRes,
    getCurrentPlanRes,
    addStripeCardRes,
    updateStripeCardRes,
    deleteStripeCardRes,
    getPlansRes,
    subscribePlanRes,
    cancelSubscriptionRes,
} from "./api";

// 🔑 Query Keys
export const subscriptionKeys = {
    root: ["subscription"] as const,
    cards: (body?: any) => [...subscriptionKeys.root, "cards", body ?? {}] as const,
    currentPlan: () => [...subscriptionKeys.root, "currentPlan"] as const,
    plans: () => [...subscriptionKeys.root, "plans"] as const,
};

export function useCurrentSubscriptionPlan() {
    return useQuery({
        queryKey: subscriptionKeys.currentPlan(),
        queryFn: async () => {
            const res = await getCurrentPlanRes();
            const body = res.data;
            const plan = body?.data ?? body ?? null;
            if (!plan || typeof plan !== "object" || plan.message || !plan.stripeSubscriptionId) {
                return null;
            }
            return plan;
        },
        staleTime: 5 * 60 * 1000,
        gcTime: 5 * 60 * 1000,
    });
}

// 💳 Fetch Stripe Cards
export function useStripeCards(body?: any) {
    return useQuery({
        queryKey: subscriptionKeys.cards(body),
        queryFn: async () => (await getStripeCardsRes(body)).data,
        placeholderData: (prev) => prev,
        staleTime: 30_000,
    });
}

// ➕ Add Card
export function useAddStripeCard() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (body: any) => {
            const response = await addStripeCardRes(body);
            return response.data;
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: subscriptionKeys.cards() });
        },
        onError: (error: any) => {
            console.error("Error adding Stripe card:", error);
        },
    });
}

// ✏️ Update Card
export function useUpdateStripeCard() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (body: any) => {
            const response = await updateStripeCardRes(body);
            return response.data;
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: subscriptionKeys.cards() });
        },
        onError: (error: any) => {
            console.error("Error updating Stripe card:", error);
        },
    });
}

// ❌ (Optional) Delete Card
export function useRemoveStripeCard() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (body: any) => {
            const response = await deleteStripeCardRes(body);
            return response.data;
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: subscriptionKeys.cards() });
        },
        onError: (error: any) => {
            console.error("Error removing Stripe card:", error);
        },
    });
}


export function useSubscriptionPlans() {
    return useQuery({
        queryKey: subscriptionKeys.plans(),
        queryFn: async () => (await getPlansRes()).data,
        staleTime: 10 * 60 * 1000, // cache for 10 min
        placeholderData: (prev) => prev,
    });
}



export function useSubscribePlan() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (body: any) => {
            const response = await subscribePlanRes(body);
            return response.data;
        },
        onSuccess: async () => {
            await Promise.all([
                queryClient.invalidateQueries({ queryKey: subscriptionKeys.currentPlan() }),
                queryClient.invalidateQueries({ queryKey: subscriptionKeys.plans() }),
                queryClient.invalidateQueries({ queryKey: subscriptionKeys.cards() }),
                queryClient.invalidateQueries({ queryKey: ["balance"] }),
            ]);
            console.info("✅ Subscription data refreshed");
        },
        onError: (error: any) => {
            console.error("Error subscribing to plan:", error);
        },
    });
}


export function useCancelSubscription() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (body: any) => {
            const response = await cancelSubscriptionRes(body);
            return response.data;
        },
        onSuccess: async () => {
            await Promise.all([
                queryClient.invalidateQueries({ queryKey: subscriptionKeys.currentPlan() }),
                queryClient.invalidateQueries({ queryKey: subscriptionKeys.plans() }),
                queryClient.invalidateQueries({ queryKey: subscriptionKeys.cards() }),
            ]);
            console.info("✅ Subscription data refreshed after cancellation");
        },
        onError: (error: any) => {
            console.error("Error cancelling subscription:", error);
        },
    });
}